import { useEffect, useRef } from 'react';
import type { Editor } from 'grapesjs';
import { useMeasurementStore } from '../stores/measurement-store';

export function useMeasurement(editor: Editor | null) {
    const measurementMode = useMeasurementStore((s) => s.measurementMode);
    const hoveredRef = useRef<any>(null);

    useEffect(() => {
        if (!editor) return;

        const canvas = editor.Canvas;
        const canvasDoc = canvas.getDocument();
        const canvasBody = canvas.getBody();

        if (!canvasDoc || !canvasBody) return;

        let overlay: HTMLDivElement | null = null;

        const createOverlay = () => {
            overlay = canvasDoc.createElement('div');
            overlay.id = 'measurement-overlay';
            overlay.style.position = 'absolute';
            overlay.style.top = '0';
            overlay.style.left = '0';
            overlay.style.width = '100%';
            overlay.style.height = '100%';
            overlay.style.pointerEvents = 'none';
            overlay.style.zIndex = '9999';
            canvasBody.appendChild(overlay);
        };

        const clearOverlay = () => {
            if (overlay) {
                overlay.innerHTML = '';
            }
        };

        const drawMeasurement = (rectA: DOMRect, rectB: DOMRect) => {
            const currentOverlay = overlay;
            if (!currentOverlay) return;
            clearOverlay();

            const drawLine = (x1: number, y1: number, x2: number, y2: number) => {
                const line = canvasDoc.createElement('div');
                const isHorizontal = y1 === y2;
                const length = isHorizontal ? Math.abs(x2 - x1) : Math.abs(y2 - y1);

                if (length <= 0) return;

                line.style.position = 'absolute';
                line.style.backgroundColor = '#fb2cac'; // Pink like Figma
                line.style.zIndex = '10000';

                if (isHorizontal) {
                    line.style.left = `${Math.min(x1, x2)}px`;
                    line.style.top = `${y1}px`;
                    line.style.width = `${length}px`;
                    line.style.height = '1px';
                } else {
                    line.style.left = `${x1}px`;
                    line.style.top = `${Math.min(y1, y2)}px`;
                    line.style.width = '1px';
                    line.style.height = `${length}px`;
                }

                const labelEl = canvasDoc.createElement('div');
                labelEl.innerText = `${Math.round(length)}`;
                labelEl.style.position = 'absolute';
                labelEl.style.backgroundColor = '#fb2cac';
                labelEl.style.color = 'white';
                labelEl.style.fontSize = '10px';
                labelEl.style.padding = '1px 3px';
                labelEl.style.borderRadius = '2px';
                labelEl.style.whiteSpace = 'nowrap';
                labelEl.style.transform = 'translate(-50%, -50%)';

                if (isHorizontal) {
                    labelEl.style.left = '50%';
                    labelEl.style.top = '-10px';
                } else {
                    labelEl.style.left = '15px';
                    labelEl.style.top = '50%';
                }

                line.appendChild(labelEl);
                currentOverlay.appendChild(line);
            };

            // Helper to draw guide lines to represent distances
            // Top
            if (rectB.bottom < rectA.top) {
                drawLine(rectA.left + rectA.width / 2, rectB.bottom, rectA.left + rectA.width / 2, rectA.top);
            } else if (rectA.bottom < rectB.top) {
                drawLine(rectA.left + rectA.width / 2, rectA.bottom, rectA.left + rectA.width / 2, rectB.top);
            }

            // Left
            if (rectB.right < rectA.left) {
                drawLine(rectB.right, rectA.top + rectA.height / 2, rectA.left, rectA.top + rectA.height / 2);
            } else if (rectA.right < rectB.left) {
                drawLine(rectA.right, rectA.top + rectA.height / 2, rectB.left, rectA.top + rectA.height / 2);
            }

            // Internal distances if B contains A or A contains B (simplified)
            if (rectB.top < rectA.top && rectB.bottom > rectA.bottom && rectB.left < rectA.left && rectB.right > rectA.right) {
                // B contains A
                drawLine(rectA.left + rectA.width / 2, rectB.top, rectA.left + rectA.width / 2, rectA.top);
                drawLine(rectA.left + rectA.width / 2, rectA.bottom, rectA.left + rectA.width / 2, rectB.bottom);
                drawLine(rectB.left, rectA.top + rectA.height / 2, rectA.left, rectA.top + rectA.height / 2);
                drawLine(rectA.right, rectA.top + rectA.height / 2, rectB.right, rectA.top + rectA.height / 2);
            }
        };

        const updateOverlay = () => {
            if (!measurementMode) {
                if (overlay) overlay.innerHTML = '';
                return;
            }

            const selected = editor.getSelected();
            const hovered = hoveredRef.current;

            if (!selected || !hovered || selected === hovered) {
                if (overlay) overlay.innerHTML = '';
                return;
            }

            const elA = selected.getEl();
            const elB = hovered.getEl();

            if (elA && elB) {
                drawMeasurement(elA.getBoundingClientRect(), elB.getBoundingClientRect());
            } else {
                if (overlay) overlay.innerHTML = '';
            }
        };

        const onHover = (model: any) => {
            if (!measurementMode) return;
            hoveredRef.current = model;
            updateOverlay();
        };

        const onMouseMove = () => {
            const canvas = editor.Canvas as any;
            const hoveredEl = canvas.getBody().querySelector(':hover');
            // getModelFromEl is internal/undocumented in some versions
            const hovered = canvas.getModelFromEl ? canvas.getModelFromEl(hoveredEl) : null;

            if (!hovered) {
                hoveredRef.current = null;
                updateOverlay();
            }
        };

        if (measurementMode) {
            if (!overlay) createOverlay();

            editor.on('component:hovered', onHover);
            editor.on('component:update', updateOverlay);
            editor.on('component:styleUpdate', updateOverlay);
            editor.on('component:resize', updateOverlay);
            editor.on('canvas:mousemove', onMouseMove);
        } else {
            const activeOverlay = canvasBody.querySelector('#measurement-overlay');
            if (activeOverlay) {
                activeOverlay.remove();
            }
            editor.off('component:hovered', onHover);
            editor.off('component:update', updateOverlay);
            editor.off('component:styleUpdate', updateOverlay);
            editor.off('component:resize', updateOverlay);
            editor.off('canvas:mousemove', onMouseMove);
            hoveredRef.current = null;
        }

        return () => {
            const activeOverlay = canvasBody.querySelector('#measurement-overlay');
            if (activeOverlay) {
                activeOverlay.remove();
            }
            editor.off('component:hovered', onHover);
            editor.off('component:update', updateOverlay);
            editor.off('component:styleUpdate', updateOverlay);
            editor.off('component:resize', updateOverlay);
            editor.off('canvas:mousemove', onMouseMove);
        };
    }, [editor, measurementMode]);
}
